/**
 * Shared Performance Lab landing-page section spacing (vh-first) and styled helpers.
 */
import type { CSSProperties, ComponentPropsWithoutRef } from 'react';
import { forwardRef } from 'react';
import {
  BodyText,
  DharmaHeading,
  DharmaTitleWrapper,
  PL_COLOR_BG,
  SectionContent,
  SectionWithBg,
} from './atoms';
import type {
  DharmaHeadingProps,
  SectionWithBgProps,
} from './atoms';
import styles from './sectionLayout.module.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/* ──────────────────── Spacing tokens ──────────────────── */

export const PL_SECTION_PAD_BLOCK_MOBILE = 'clamp(80px, 12svh, 120px)';
export const PL_SECTION_PAD_BLOCK_DESKTOP = 'clamp(120px, 16svh, 160px)';
export const PL_SECTION_PAD_INLINE_MOBILE = '16px';
export const PL_SECTION_PAD_INLINE_DESKTOP = 'clamp(24px, 8vw, 120px)';
export const PL_SECTION_HEADER_GAP = '16px';
export const PL_SECTION_HEADER_TO_CONTENT_MOBILE = 'clamp(48px, 8svh, 80px)';
export const PL_SECTION_HEADER_TO_CONTENT_DESKTOP = 'clamp(64px, 8svh, 80px)';
export const PL_PILLAR_GAP = '24px';
export const PL_PILLAR_STACK_GAP_MOBILE = '32px';
export const PL_DIVIDER_COLOR = 'rgba(0, 0, 0, 0.12)';

/* ──────────────────── Layout shells ──────────────────── */

export const PlSectionRoot = forwardRef<HTMLElement, SectionWithBgProps>(
  function PlSectionRoot({ className, ...rest }, ref) {
    return (
      <SectionWithBg
        ref={ref}
        className={cx(styles.plSectionRoot, className)}
        {...rest}
      />
    );
  },
);

export const PlContentShell = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function PlContentShell({ className, ...rest }, ref) {
  return (
    <div ref={ref} className={cx(styles.plContentShell, className)} {...rest} />
  );
});

export const PlContentShellCentered = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function PlContentShellCentered({ className, ...rest }, ref) {
  return (
    <PlContentShell
      ref={ref}
      className={cx(styles.plContentShellCentered, className)}
      {...rest}
    />
  );
});

/* ──────────────────── Section header ──────────────────── */

export const PlSectionLabel = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'>
>(function PlSectionLabel({ className, ...rest }, ref) {
  return (
    <p ref={ref} className={cx(styles.plSectionLabel, className)} {...rest} />
  );
});

export interface PlSectionHeaderProps extends ComponentPropsWithoutRef<'div'> {
  $alignDesktop?: 'left' | 'center';
  $maxWidth?: string;
}

export const PlSectionHeader = forwardRef<HTMLDivElement, PlSectionHeaderProps>(
  function PlSectionHeader(
    { className, style, $alignDesktop, $maxWidth, ...rest },
    ref,
  ) {
    return (
      <div
        ref={ref}
        data-align-desktop={$alignDesktop}
        style={
          $maxWidth
            ? ({ ...style, '--pl-header-max-width': $maxWidth } as CSSProperties)
            : style
        }
        className={cx(styles.plSectionHeader, className)}
        {...rest}
      />
    );
  },
);

export const PlSectionHeading = forwardRef<
  HTMLHeadingElement,
  DharmaHeadingProps
>(function PlSectionHeading({ className, ...rest }, ref) {
  return (
    <DharmaHeading
      ref={ref}
      className={cx(styles.plSectionHeading, className)}
      {...rest}
    />
  );
});

export const PlTitleStack = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function PlTitleStack({ className, ...rest }, ref) {
  return (
    <DharmaTitleWrapper
      ref={ref}
      className={cx(styles.plTitleStack, className)}
      {...rest}
    />
  );
});

/* ──────────────────── Pillar / card copy ──────────────────── */

export const PlPillarTitle = forwardRef<
  HTMLHeadingElement,
  ComponentPropsWithoutRef<'h3'>
>(function PlPillarTitle({ className, ...rest }, ref) {
  return (
    <h3 ref={ref} className={cx(styles.plPillarTitle, className)} {...rest} />
  );
});

export const PlPillarBody = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'>
>(function PlPillarBody({ className, ...rest }, ref) {
  return (
    <p ref={ref} className={cx(styles.plPillarBody, className)} {...rest} />
  );
});

export const PlPillarEmphasis = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'>
>(function PlPillarEmphasis({ className, ...rest }, ref) {
  return (
    <p ref={ref} className={cx(styles.plPillarEmphasis, className)} {...rest} />
  );
});

/* ──────────────────── Dividers ──────────────────── */

export const PlDividerVertical = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function PlDividerVertical({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cx(styles.plDividerVertical, className)}
      {...rest}
    />
  );
});

export const PlDividerHorizontal = forwardRef<
  HTMLHRElement,
  ComponentPropsWithoutRef<'hr'>
>(function PlDividerHorizontal({ className, ...rest }, ref) {
  return (
    <hr
      ref={ref}
      className={cx(styles.plDividerHorizontal, className)}
      {...rest}
    />
  );
});

/* Re-export for convenience */
export { SectionContent, BodyText, PL_COLOR_BG };
